import * as React from "react";
import classNames from "classnames";
import { DomTranslator, translate } from "utils";
import { Dialog } from "@stemscopes/ui";
import { useCallback, useEffect, useRef, useState } from "react";
import { Dropdown, Icon, Typography, Menu, MenuItem } from "components";
import { useDispatch } from "react-redux";
import { useDebounce } from "hooks";
import styles from "./TranslateSwitcher.module.css";
import { languages } from "./duck/constants";
import * as actions from "../../../../components/toolbar/components/actions/components/highlighting/duck/actions";
import { TranslateLoader } from "../translate-loader";

const OtherLanguageItem: React.FC<{
  code: string;
  label: string;
  onChange: (code: string) => void;
}> = ({ code, label, onChange }) => {
  const languageFromLS = localStorage.getItem("language") || "en";

  return (
    <MenuItem
      className={classNames(styles.otherLanguageItem, "notranslate", {
        [styles.selected]: languageFromLS === code,
      })}
      onClick={() => onChange(code)}
      title={`Translated to ${label}`}
    >
      <Typography variant="p2" component="span">
        {label}{" "}
        {languageFromLS === code ? (
          <Icon name="check-circle-outlined" size="small" />
        ) : undefined}
      </Typography>
    </MenuItem>
  );
};

function useRequestFlag<T>(
  asyncFn: (params: any) => Promise<T>,
): [boolean, (params: any) => Promise<T>] {
  const [isRunning, setIsRunning] = useState(false);
  const debounceSetter = useDebounce(() => setIsRunning(false), {
    timeoutMs: 1500,
  });
  const wrappedFn = useCallback(
    async (params: any) => {
      setIsRunning(true);
      try {
        return await asyncFn(params);
      } finally {
        debounceSetter();
      }
    },
    [asyncFn],
  );

  return [isRunning, wrappedFn];
}

export const TranslateSwitcher: React.FC<{
  className?: string;
}> = ({ className }) => {
  const languageFromLS = localStorage.getItem("language") || "en";
  const translator = useRef<InstanceType<typeof DomTranslator> | undefined>(
    undefined,
  );
  const [open, setOpen] = React.useState(false);
  const dispatch = useDispatch();
  const [isLoading, runRequest] = useRequestFlag(translate);

  const handleSwitchLanguage = (value: string): void => {
    translator?.current?.switchLanguage(value);
  };

  const initSelections = (): void => {
    dispatch(actions.initSelections());
  };

  useEffect(() => {
    translator.current = new DomTranslator({
      translateAPI: (texts: string[]) =>
        runRequest({
          value: texts,
          languageCode: languageFromLS,
        }),
      initSelections,
    });

    // @ts-ignore
    window.translator = translator.current;
    return () => {
      translator?.current?.disconnect();
    };
  }, []);

  return (
    <>
      <Dropdown
        size="small"
        variant="clear"
        position="right"
        title="Expand translation menu"
        label={
          <Typography variant="p2" component="span" className="notranslate">
            {languages.find((item) => item.id === languageFromLS)?.name}
          </Typography>
        }
        buttonClassName={classNames(className, styles.btn, "notranslate")}
        className={(opened) => (opened ? styles.opened : "")}
        contentContainerClassName={styles.content}
        suffix={
          <Icon
            name="down-outlined"
            size="small"
            className={styles.iconSuffix}
          />
        }
      >
        <Menu popup orientation="vertical" hoverable={false}>
          <MenuItem
            className={classNames(styles.item, "notranslate")}
            title="Translated to English"
            onClick={() => translator.current?.switchLanguage("en")}
          >
            <Typography variant="p2" component="span">
              English{" "}
              {languageFromLS === "en" ? (
                <Icon name="check-circle-outlined" size="small" />
              ) : undefined}
            </Typography>
          </MenuItem>
          <MenuItem
            className={classNames(styles.item, "notranslate")}
            title="Traducir al español"
            onClick={() => translator.current?.switchLanguage("es")}
          >
            <Typography variant="p2" component="span">
              Español{" "}
              {languageFromLS === "es" ? (
                <Icon name="check-circle-outlined" size="small" />
              ) : undefined}
            </Typography>
          </MenuItem>
          <MenuItem
            className={styles.item}
            title="Show list of other available languages"
            onClick={() => setOpen(true)}
          >
            <Typography variant="p2" component="span">
              Other Languages{" "}
              {!["en", "es"].includes(languageFromLS) ? (
                <Icon name="check-circle-outlined" size="small" />
              ) : undefined}
            </Typography>
          </MenuItem>
        </Menu>
      </Dropdown>
      <Dialog
        title="Select Language"
        opened={open}
        onClose={() => setOpen(false)}
        className={styles.dialog}
      >
        <Menu className={styles.otherLanguages}>
          {languages
            .filter((item) => !["en", "es"].includes(item.id))
            .map((language) => (
              <OtherLanguageItem
                key={language.id}
                code={language.id}
                label={language.name}
                onChange={handleSwitchLanguage}
              />
            ))}
        </Menu>
      </Dialog>
      <TranslateLoader isLoading={isLoading} />
    </>
  );
};
